/**
 * Client-side mirror of the rules `mergeContractors` applies in
 * `cims-backend/src/api/contractor/controllers/contractor.ts` (and
 * `mergePseaAssessments` in `cims-backend/src/api/psea-assessment/pseaMerge.ts`).
 * They only drive the preview on the merge page; the server remains the
 * source of truth, so keep the two in step.
 */

const normalize = (v: any) =>
  (typeof v === "string" ? v : v?.toString?.() ?? "").trim().toLowerCase();

/**
 * Appends incoming items whose key is not already present. Existing items are
 * always kept as they are; incoming items with an empty key are dropped.
 * Each result item is tagged with where it came from so the preview can mark
 * what the duplicate contributes.
 */
export const mergeUniqueBy = (
  existing: any[] = [],
  incoming: any[] = [],
  key: string
) => {
  const seen = new Set(
    (existing || []).map((item) => normalize(item?.[key])).filter(Boolean)
  );
  const additions = (incoming || []).filter((item) => {
    const val = normalize(item?.[key]);
    if (!val || seen.has(val)) return false;
    seen.add(val);
    return true;
  });
  return [
    ...(existing || []).map((item) => ({ item, fromDuplicate: false })),
    ...additions.map((item) => ({ item, fromDuplicate: true })),
  ];
};

const assessedTime = (assessment: any) =>
  assessment?.dateOfAssessment
    ? new Date(assessment.dateOfAssessment).getTime()
    : null;

/**
 * Which record's PSEA assessment survives when both have one. The primary
 * record wins ties and cases where neither assessment is dated.
 */
export const pseaKeptSide = (
  primaryPsea: any,
  duplicatePsea: any
): "primary" | "duplicate" => {
  const primaryTime = assessedTime(primaryPsea);
  const duplicateTime = assessedTime(duplicatePsea);
  if (duplicateTime === null) return "primary";
  if (primaryTime === null) return "duplicate";
  return duplicateTime > primaryTime ? "duplicate" : "primary";
};

/** True when the kept PSEA assessment was chosen because it is more recent. */
export const pseaDecidedByDate = (primaryPsea: any, duplicatePsea: any) =>
  assessedTime(primaryPsea) !== assessedTime(duplicatePsea);

export const formatAddress = (el: any) =>
  [el?.address, el?.city, el?.country].filter(Boolean).join(", ");
